/**
 * Generic utility for grouping data by a key field and creating separate fields for each value of a category field
 *
 * @param rawData - Array of data items to group
 * @param groupKey - Field name to group by (e.g., 'business_unit_id')
 * @param categoryField - Field name for the category to create separate fields (e.g., 'project_status_group')
 * @param valueField - Field name for the numeric value (e.g., 'project_count')
 * @returns Object with transformed data and metadata
 */
export interface GroupedDataResult<T = Record<string, unknown>> {
  transformedData: T[];
  uniqueCategories: string[];
}

export function groupDataByCategory<T extends Record<string, unknown>>({
  rawData,
  groupKey,
  displayField,
  categoryField,
  valueField,
}: {
  rawData: T[];
  groupKey: keyof T;
  displayField?: keyof T;
  categoryField: keyof T;
  valueField: keyof T;
}): GroupedDataResult<T> {
  // Group data by the specified field
  const groupedData = rawData.reduce((acc, item) => {
    const groupKeyValue = String(item[groupKey]);
    const categoryValue = item[categoryField];
    const numericValue = item[valueField];

    if (!acc[groupKeyValue]) {
      acc[groupKeyValue] = {
        [groupKey]: groupKeyValue,
      } as Record<string, unknown>;

      // Add display field if provided
      if (displayField) {
        (acc[groupKeyValue] as Record<string, unknown>)[String(displayField)] =
          item[displayField];
      }
    }

    // Use the category value directly as the field name
    (acc[groupKeyValue] as Record<string, unknown>)[String(categoryValue)] =
      numericValue;

    return acc;
  }, {} as Record<string, Record<string, unknown>>);

  // Convert to array and get unique categories
  const transformedData = Object.values(groupedData) as T[];
  const uniqueCategories = [
    ...new Set(rawData.map((item) => String(item[categoryField]))),
  ];

  return {
    transformedData,
    uniqueCategories,
  };
}
